// Article "Passer le permis A1 ou A2" — Vol. 20 / Mai 2026
// Source : sécurité-routière.gouv.fr (page officielle "Passer le permis A1 ou A2")
// Article phare publié à la une du site CODE 2 ROUES.

import {
  GraduationCap,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Bike,
  ClipboardCheck,
  BookOpen,
  Lightbulb,
  Map,
  Trophy,
  Target,
  PlayCircle,
} from 'lucide-react';

export const permisA1A2Article = {
  slug: 'passer-le-permis-a1-a2',
  weekVolume: 20,
  weekYear: 2026,
  monthLabel: 'Mai 2026',
  category: 'Préparation au permis moto',
  shortTitle: 'Permis A1 / A2 — Le guide complet',
  title: 'Passer le permis A1 ou A2',
  kicker: 'Le guide officiel pour passer ton permis moto',
  subtitle:
    "Conditions d'âge, examen du code (ETM), épreuve plateau, circulation, notation : toutes les étapes pour décrocher ton permis A1 ou A2 en 2026, conformes au programme officiel de la Sécurité routière.",
  heroImage:
    'https://images.unsplash.com/photo-1558981359-219d6364c9c8?w=2400&q=80&auto=format&fit=crop',
  heroImageAlt:
    'Jeune motard équipé prêt à passer son examen du permis moto A1 ou A2 — moto-école',
  topRightKicker: { line1: 'Guide officiel', line2: 'Sécurité routière 2026' },
  readTimeMin: 7,
  seoDescription:
    "Passer son permis moto A1 ou A2 en 2026 : conditions d'âge, examen du code ETM, épreuve plateau (6 manœuvres), circulation, notation A/B/C. Le guide complet et officiel CODE 2 ROUES.",
  seoKeywords:
    'permis A1, permis A2, passer permis moto, ETM 2026, plateau permis moto, code moto, conditions âge permis moto, équipement obligatoire examen',
  about:
    'Permis moto A1 et A2, programme officiel ETM 2026, examen plateau et circulation',
  source: {
    label: 'Sécurité routière',
    url: 'https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2',
    refs: 'Arrêté du 23 avril 2012 modifié — Référentiel pour l\'éducation à la mobilité citoyenne (REMC).',
  },
  chapo: {
    kicker: 'Pourquoi cet article',
    body: "Tu veux conduire une moto ? Il existe <strong>5 catégories</strong> de permis selon la cylindrée et la puissance. Pour la grande majorité des candidats, deux permis dominent : le <strong>A1</strong> (à partir de 16 ans, jusqu'à 125&nbsp;cm³) et le <strong>A2</strong> (à partir de 18 ans, jusqu'à 35&nbsp;kW). Ce guide t'accompagne <em>de l'inscription à l'obtention</em>, en respectant à la lettre le programme officiel de la Sécurité routière.",
    sideStat: {
      kicker: 'Le chiffre',
      big: '10 min',
      body: "C'est la durée de <strong>l'épreuve plateau</strong>. Un parcours unique de <strong>6 manœuvres</strong> qui s'enchaînent. Chaque seconde compte.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text:
      "La moto, c'est la liberté. Mais la liberté commence par la maîtrise. Chaque manœuvre apprise au plateau, c'est une seconde de plus pour réagir sur la route.",
    source: 'Équipe pédagogique CODE 2 ROUES',
  },
  sections: [
    {
      part: 'Partie 1 — Quel permis pour quelle moto',
      title: 'Choisir le bon permis selon ta moto',
      intro:
        "Le code de la route distingue <strong>5 catégories</strong> de permis pour les deux-roues motorisés. Voici comment t'y retrouver.",
      cards: [
        {
          icon: Bike,
          accent: '#1d4ed8',
          kicker: 'Permis AM',
          title: 'Cyclomoteurs (≤ 50 cm³)',
          body:
            "À partir de <strong>14 ans</strong>. Pour conduire un scooter ou une mob ≤ 50 cm³ ou ≤ 4 kW. Inclus dans la formation BSR (8h).",
        },
        {
          icon: Bike,
          accent: '#16a34a',
          kicker: 'Permis A1',
          title: 'Motos légères (≤ 125 cm³)',
          body:
            "À partir de <strong>16 ans</strong>. Motos jusqu'à 125 cm³ et 11 kW. Tricycles ≤ 15 kW. Examen complet (code + plateau + circulation).",
        },
        {
          icon: Bike,
          accent: '#cf2031',
          kicker: 'Permis A2',
          title: 'Motos intermédiaires (≤ 35 kW)',
          body:
            "À partir de <strong>18 ans</strong>. Motos jusqu'à <strong>35 kW</strong> (≈ 47 ch). C'est le permis obligatoire pour tout primo-accédant adulte.",
        },
        {
          icon: Bike,
          accent: '#7c3aed',
          kicker: 'Permis A',
          title: 'Toutes motos',
          body:
            "Accès après <strong>2 ans de A2</strong> + formation passerelle de <strong>7h</strong>. Permet de conduire toutes les cylindrées et puissances.",
        },
      ],
      cardsCols: 2,
      callouts: [
        {
          icon: Lightbulb,
          accent: '#1d4ed8',
          body:
            "<strong>Bon à savoir :</strong> si tu as ton permis B (voiture) depuis au moins <strong>2 ans</strong>, tu peux conduire une 125 cm³ après une <strong>formation pratique de 7h</strong> en moto-école — sans passer le permis A1.",
        },
      ],
    },
    {
      part: 'Partie 2 — Les conditions',
      title: "Conditions d'accès et pré-requis",
      intro:
        "Avant de t'inscrire en moto-école, vérifie que tu remplis bien <strong>toutes</strong> les conditions ci-dessous.",
      cards: [
        {
          icon: Calendar,
          accent: '#1d4ed8',
          kicker: 'Âge minimum',
          title: '16 ans (A1) / 18 ans (A2)',
          body:
            "Tu peux <strong>commencer la formation 6 mois avant</strong> ton anniversaire. Mais l'examen pratique ne peut être passé qu'à partir de l'âge réglementaire.",
        },
        {
          icon: ShieldCheck,
          accent: '#16a34a',
          kicker: 'ASSR / ASR',
          title: 'Attestation scolaire',
          body:
            "L'<strong>ASSR2</strong> (collège) ou l'<strong>ASR</strong> (centre AFPA) est obligatoire pour les candidats nés <strong>après 1988</strong>. Demande-la à ton établissement.",
        },
        {
          icon: BookOpen,
          accent: '#cf2031',
          kicker: 'Formation théorique',
          title: 'Code moto (ETM)',
          body:
            "Examen théorique <strong>spécifique moto</strong> (différent du code voiture) : 40 questions sur les spécificités du deux-roues motorisé. <strong>5 ans de validité.</strong>",
        },
        {
          icon: GraduationCap,
          accent: '#7c3aed',
          kicker: 'Formation pratique',
          title: 'Au minimum 20h',
          body:
            "Dont <strong>8h de plateau</strong> et <strong>12h de circulation</strong>. Tarif moyen <strong>900 € à 1 200 €</strong> selon la moto-école et la région.",
        },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 3 — Le code moto',
      title: "L'examen théorique moto (ETM)",
      intro:
        "Depuis 2020, le permis moto a son <strong>propre code</strong> : l'ETM (Examen Théorique Moto). Il remplace l'ETG (le code voiture) et porte exclusivement sur les <strong>spécificités du deux-roues motorisé</strong>.",
      compareCards: [
        {
          accent: '#16a34a',
          kicker: 'Format de l\'épreuve',
          title: '40 questions · 35 bonnes réponses minimum',
          intro:
            "Sur écran ou tablette, en centre agréé. Durée environ 30 minutes.",
          points: [
            { ok: true, text: 'Questions à choix multiples (1 ou plusieurs bonnes réponses)' },
            { ok: true, text: 'Vidéos, photos, schémas et situations de circulation' },
            { ok: true, text: 'Tarif unique : <strong>30 €</strong> par tentative' },
            { ok: true, text: '<strong>5 ans de validité</strong> avant de devoir le repasser' },
          ],
        },
        {
          accent: '#1d4ed8',
          kicker: '9 thèmes officiels',
          title: 'Le programme ETM 2026',
          intro:
            "L'épreuve couvre l'ensemble des thèmes du programme officiel.",
          points: [
            { ok: true, text: 'Circulation routière · Conducteur' },
            { ok: true, text: 'Route (signalisation, infrastructures)' },
            { ok: true, text: 'Autres usagers (vulnérables, partage)' },
            { ok: true, text: 'Notions diverses, premiers secours' },
            { ok: true, text: 'Précautions de prise en main du véhicule' },
            { ok: true, text: 'Mécanique et équipements' },
            { ok: true, text: 'Environnement et éco-conduite' },
          ],
        },
      ],
      callouts: [
        {
          icon: PlayCircle,
          accent: '#cf2031',
          body:
            "<strong>🎥 Vidéo officielle <em>« Le permis moto change »</em></strong> · Sécurité routière, 1:35 · <a href='https://www.youtube.com/watch?v=v3MaLekJ0vU' target='_blank' rel='noopener noreferrer' class='underline font-bold text-[hsl(var(--primary))]'>Regarder sur YouTube ↗</a>" +
            "<br/><br/>" +
            "<strong>📝 11 questions d'exemples ETM officielles</strong> · disponibles en vidéo + correction PDF officielle sur le site Sécurité Routière :" +
            "<div class='mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] font-bold'>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q1 · 1ers secours · 0:45</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q2 · Inter-files · 0:49</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q3 · Conduite apaisée</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q4 · Dépassement+intersection · 0:59</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q5 · Vérification véhicule · 0:42</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q6 · Gilet airbag · 0:45</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q7 · Trajectoire sécurité · 0:43</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q8 · Placement sur route · 0:35</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q9 · Visibilité du motard · 0:52</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q10 · Dépassement+visibilité · 0:35</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2#révisionpermismoto' target='_blank' rel='noopener noreferrer' class='inline-flex items-center justify-center px-2 py-1.5 rounded border bg-white/60 hover:bg-white text-foreground/85'>Q11 · Intersection · 0:35</a>" +
            "</div>" +
            "<div class='mt-3 flex flex-wrap gap-2'>" +
            "<a href='https://www.securite-routiere.gouv.fr/sites/default/files/2020-03/reponsesquestionspermismoto2020.pdf' target='_blank' rel='noopener noreferrer' class='inline-flex items-center gap-1.5 text-[11px] font-black px-3 py-1.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90'>📥 Télécharger les 11 réponses (PDF officiel · 581 Ko)</a>" +
            "<a href='https://www.securite-routiere.gouv.fr/sites/default/files/2019-04/fichecontrole_quelpermispourquel2rm_2019_exe_bd_1.pdf' target='_blank' rel='noopener noreferrer' class='inline-flex items-center gap-1.5 text-[11px] font-black px-3 py-1.5 rounded-full border bg-white/60 hover:bg-white text-foreground/85'>📥 Fiche contrôle 2RM/3RM (PDF · 530 Ko)</a>" +
            "</div>",
        },
      ],
    },
    {
      part: 'Partie 4 — L\'épreuve plateau',
      title: 'Le plateau : 6 manœuvres en 10 minutes',
      intro:
        "Première épreuve pratique. <strong>10 minutes</strong> sur un terrain fermé pour démontrer ta maîtrise du véhicule à allure lente comme à vitesse normale, sans passager puis avec passager. <strong>Un parcours unique, 6 manœuvres qui s'enchaînent.</strong>",
      cards: [
        {
          icon: Target,
          accent: '#7dd3fc',
          kicker: 'Manœuvre 1',
          title: 'Déplacer la moto sans le moteur',
          body:
            'Débéquiller, pousser en marche avant, reculer en marche arrière, rebéquiller. Évalue ta maîtrise du gabarit et du poids.',
        },
        {
          icon: Target,
          accent: '#86efac',
          kicker: 'Manœuvre 2',
          title: 'Allure réduite sans passager',
          body:
            "Parcours sinueux à allure lente, <strong>sans pied au sol</strong> et <strong>sans arrêt</strong>. Partie chronométrée entre A et B.",
        },
        {
          icon: Target,
          accent: '#fde68a',
          kicker: 'Manœuvre 3',
          title: "Freinage d'urgence",
          body:
            "Accélération à <strong>50 km/h minimum</strong> en C6 puis freinage d'urgence : arrêt avant la ligne C5 (sec) ou C4 (mouillé).",
        },
        {
          icon: Target,
          accent: '#3b82f6',
          kicker: 'Manœuvre 4',
          title: 'Allure réduite avec passager',
          body:
            "Reprend le passager dans la zone de freinage, effectue le parcours puis le dépose entre la porte « piquets » et la porte « cônes ».",
        },
        {
          icon: Target,
          accent: '#dc2626',
          kicker: 'Manœuvre 5',
          title: 'Slalom',
          body:
            "Slalom serré entre les cônes à <strong>40 km/h minimum</strong> au niveau de la ligne C7. Demi-tour franc en bout de piste.",
        },
        {
          icon: Target,
          accent: '#dc2626',
          kicker: 'Manœuvre 6',
          title: 'Évitement',
          body:
            "Évitement franc à <strong>50 km/h minimum</strong> au niveau de C6, puis arrêt dans la zone dédiée. <strong>Aucun cône touché</strong> sous peine d'élimination.",
        },
      ],
      cardsCols: 2,
      callouts: [
        {
          icon: Map,
          accent: '#cf2031',
          body:
            "<strong>📍 Sur CODE 2 ROUES :</strong> retrouve le <a href='/epreuves/plateau' class='underline font-bold text-[hsl(var(--primary))]'>schéma officiel détaillé du parcours plateau</a> avec le déroulé pas-à-pas des 6 manœuvres et 2 vidéos tutoriels gratuites.",
        },
      ],
    },
    {
      part: 'Partie 5 — La circulation',
      title: "L'épreuve de circulation",
      intro:
        "Seconde épreuve pratique : <strong>30 minutes minimum</strong> de conduite sur route ouverte avec un inspecteur du permis de conduire. Tu dois démontrer ta capacité à conduire en sécurité dans un trafic réel.",
      cards: [
        {
          icon: ClipboardCheck,
          accent: '#1d4ed8',
          kicker: 'Avant le départ',
          title: 'Vérification mécanique',
          body:
            "Tu réponds à <strong>2 questions tirées au sort</strong> sur l'entretien et les points de sécurité de la moto (phares, freins, pneus, niveaux...).",
        },
        {
          icon: Bike,
          accent: '#16a34a',
          kicker: 'Pendant la conduite',
          title: 'Itinéraire varié',
          body:
            "Ville, route, voies rapides si possible. L'inspecteur évalue ta <strong>trajectoire, allure, observation, anticipation, partage de la chaussée</strong> et <strong>respect du code</strong>.",
        },
        {
          icon: Lightbulb,
          accent: '#cf2031',
          kicker: 'Conduite autonome',
          title: '5 minutes guidées GPS',
          body:
            "Tu conduis pendant 5 min en suivant des indications GPS ou un itinéraire annoncé, sans guidage de l'inspecteur. Mesure ton autonomie de décision.",
        },
        {
          icon: Trophy,
          accent: '#7c3aed',
          kicker: 'Bilan final',
          title: 'Auto-évaluation',
          body:
            "L'inspecteur peut te demander de <strong>commenter ta propre conduite</strong>. Sois honnête : reconnaître une erreur est un signe de maturité, ce n'est pas pénalisé.",
        },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 6 — La notation',
      title: 'Comment es-tu noté ?',
      intro:
        "L'inspecteur applique une <strong>grille nationale</strong> avec 3 niveaux par critère. Voici ce qu'il regarde et comment il décide.",
      normCards: [
        {
          code: 'A',
          color: '#16a34a',
          title: 'Acquis',
          body:
            "La compétence est <strong>maîtrisée</strong>. Tu es à l'aise et autonome.",
        },
        {
          code: 'B',
          color: '#eab308',
          title: 'En cours d\'acquisition',
          body:
            "Tu connais la compétence mais tu manques <strong>encore d'aisance</strong>. Acceptable.",
        },
        {
          code: 'C',
          color: '#cf2031',
          title: 'Non acquis',
          body:
            "La compétence n'est pas maîtrisée : <strong>pénalisant</strong>, parfois éliminatoire.",
        },
      ],
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body:
            "<strong>Conditions de réussite</strong> : aucune note <strong>C éliminatoire</strong> (ex. cône touché à l'évitement, vitesse non atteinte au radar) + <strong>17 points minimum sur 27</strong> en circulation. Sécurité, regard et anticipation pèsent le plus lourd.",
        },
      ],
    },
    {
      part: 'Partie 7 — Et après ?',
      title: 'Période probatoire et passerelle vers le permis A',
      paragraphs: [
        "Une fois le permis obtenu, tu entres dans la <strong>période probatoire</strong> : ton permis ne dispose d'abord que de <strong>6 points</strong>, qui passent à 12 progressivement (8 après 1 an, 10 après 2 ans, 12 après 3 ans <em>sans infraction</em>).",
        "Le <strong>permis A2</strong> donne accès aux motos jusqu'à <strong>35 kW</strong>. Pour passer aux motos plus puissantes, il faudra attendre <strong>2 ans de pratique</strong> puis effectuer une <strong>formation passerelle de 7 heures</strong> pour obtenir le permis A.",
      ],
      cards: [
        {
          icon: Calendar,
          accent: '#1d4ed8',
          kicker: 'Année 1',
          title: '6 → 8 points',
          body:
            "À l'obtention du permis, tu démarres avec 6 points. <strong>Aucune infraction</strong> pendant 1 an = 8 points.",
        },
        {
          icon: Calendar,
          accent: '#16a34a',
          kicker: 'Année 2',
          title: '8 → 10 points',
          body:
            "2ᵉ année probatoire sans infraction = 10 points. Tu peux préparer la <strong>passerelle A2 → A</strong>.",
        },
        {
          icon: Calendar,
          accent: '#cf2031',
          kicker: 'Année 3',
          title: '10 → 12 points',
          body:
            "Sans infraction, tu termines la période probatoire avec un <strong>permis plein 12 points</strong>.",
        },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: CheckCircle2,
          accent: '#16a34a',
          body:
            "<strong>Conduite accompagnée moto :</strong> elle <strong>n'existe pas</strong>, contrairement à la voiture. Pour le permis moto, il faut suivre la formation classique en moto-école.",
        },
      ],
    },
  ],
  quiz: [
    {
      id: 'q1',
      question: "À partir de quel âge peut-on passer le permis A2 ?",
      options: ['16 ans', '17 ans', '18 ans', '21 ans'],
      correctIndex: 2,
      explain:
        'Le permis A2 est accessible dès 18 ans. La formation peut débuter 6 mois avant, soit dès 17 ans et 6 mois.',
    },
    {
      id: 'q2',
      question: "Combien de manœuvres comporte l'épreuve plateau ?",
      options: ['4 manœuvres', '5 manœuvres', '6 manœuvres', '8 manœuvres'],
      correctIndex: 2,
      explain:
        "L'épreuve plateau comporte 6 manœuvres qui s'enchaînent : déplacement sans moteur, allure réduite sans passager, freinage d'urgence, allure réduite avec passager, slalom, évitement.",
    },
    {
      id: 'q3',
      question: "Quelle est la vitesse minimale au radar C6 pour le freinage d'urgence ?",
      options: ['30 km/h', '40 km/h', '50 km/h', '60 km/h'],
      correctIndex: 2,
      explain:
        "À hauteur de la ligne C6, la vitesse mesurée doit être de 50 km/h minimum (avec une marge de 5 km/h en A1). Le freinage doit ensuite immobiliser la moto avant la ligne C5 (sec) ou C4 (mouillé).",
    },
    {
      id: 'q4',
      question: "Combien de bonnes réponses sont nécessaires pour réussir l'examen ETM ?",
      options: ['30 sur 40', '32 sur 40', '35 sur 40', '40 sur 40'],
      correctIndex: 2,
      explain:
        "Il faut au moins 35 bonnes réponses sur 40 pour valider l'examen théorique moto (ETM), valable ensuite 5 ans.",
    },
    {
      id: 'q5',
      question: 'Que signifie une note "C" dans la grille de notation officielle ?',
      options: [
        'Compétence acquise',
        'Compétence en cours d\'acquisition',
        'Compétence non acquise (souvent pénalisant)',
        'Compétence non évaluée',
      ],
      correctIndex: 2,
      explain:
        "La note C signifie « non acquis » : la compétence n'est pas maîtrisée. Selon le critère, ce peut être éliminatoire (cône touché, vitesse non atteinte) ou simplement pénalisant.",
    },
  ],
  ctaFinal: {
    kicker: 'Passe à la pratique',
    title: 'Prêt à attaquer le plateau ?',
    body:
      "Le plateau ne s'apprend pas dans les livres : il se vit, se chronomètre, se répète. Découvre le schéma officiel du parcours et les vidéos tutos commentées par des moniteurs.",
    primary: { label: "Voir le parcours plateau", to: '/epreuves/plateau' },
    secondary: { label: "Tester l'examen ETM blanc", to: '/examen' },
  },
};
